import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map, scan } from 'rxjs';

import { SegmentTrackEvent } from '@ninety/platform/models/analytics/segment/segment-track-event.enum';
import { TrashToolSegmentType } from '@ninety/platform/models/analytics/segment/trash-tool-segment-type.enum';
import { FilterServiceActions } from '@ninety/platform/services';
import { selectCurrentUserAsCompanyUser } from '@ninety/platform/state';
import { trackArchiveViewToggled } from '@ninety/platform/state/app-global/analytics/segment/archive-segment.helpers';
import { selectArchiveTrackingMeeting } from '@ninety/platform/state/app-global/analytics/segment/archive-segment.selectors';
import { SegmentActions } from '@ninety/platform/state/app-global/analytics/segment/segment.actions';
import {
  trackTrashDeleteForever,
  trackTrashRestored,
  trackTrashViewed,
} from '@ninety/platform/state/app-global/analytics/segment/trash-segment.helpers';

import { HeadlinesStateActions } from './headlines-state.actions';
import { selectIsHeadlinesUrl } from './headlines-state.selectors';

/** Fields that route through the generic `update` action but are NOT content edits, so they're
 *  excluded from headline_updated. Commenting (the comment list dispatches update({ comments }))
 *  is engagement, not an edit, and isn't a tracked event. After exclusion an empty change set
 *  emits nothing (the filter below) — also guarding the no-op-update case. */
const HEADLINE_NON_EDIT_FIELDS = ['comments'];

@Injectable()
export class HeadlinesAnalyticsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  /** Universal Create dispatches both `add` (state mutation) and `createForSegment` (the dedicated
   *  analytics trigger) for one headline, so listening to both double-fires headline_created — and
   *  `add` is gated on the team filter, making the count depend on UI state. `createForSegment` is
   *  the canonical trigger (carries the persisted headline with its real `_id`); track only that. */
  trackHeadlineCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.createForSegment),
      map(({ headline }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.HEADLINE_CREATED,
          params: {
            entityId: headline._id,
            companyId: headline.companyId ?? '',
            teamId: headline.teamId,
            userId: headline.userId,
          },
        })
      )
    )
  );

  /** Headline Edited — fires on the local user's own content edit (update → updateSuccess).
   *  Realtime edits from other users route through updateLocal (not updateSuccess) so this never
   *  misattributes; completion/user-change have their own dedicated actions so no double-fire. */
  trackHeadlineUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.updateSuccess),
      map(({ update }) => ({
        update,
        changedFields: Object.keys(update.changes).filter(f => !HEADLINE_NON_EDIT_FIELDS.includes(f)),
      })),
      filter(({ changedFields }) => changedFields.length > 0),
      map(({ update, changedFields }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.HEADLINE_UPDATED,
          params: {
            entityId: update.id as string,
            changedFields: changedFields.join(','),
          },
        })
      )
    )
  );

  trackHeadlineCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.setCompletedSuccess, HeadlinesStateActions.markCompletedForSegment),
      concatLatestFrom(() => this.store.select(selectCurrentUserAsCompanyUser)),
      map(([{ update }, currentCompanyUser]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.HEADLINE_SET_COMPLETED,
          params: {
            entityId: update.id,
            companyId: currentCompanyUser?.company?._id,
            headlineCompletedFlag: update.changes.isDone,
            headlineCompletedDate: update.changes.completedDate,
          },
        })
      )
    )
  );

  /** Only on false → true; redundant setShowTrash(true) must not double-fire View Trash. */
  trackTrashViewed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.setShowTrash),
      scan(
        (acc, { show }) => {
          const shouldTrack = !acc.previous && show;
          return { previous: show, shouldTrack };
        },
        { previous: false, shouldTrack: false }
      ),
      filter(({ shouldTrack }) => shouldTrack),
      map(() => trackTrashViewed(TrashToolSegmentType.Headlines))
    )
  );

  trackTrashRestored$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.restoreHeadline),
      map(({ headlineId }) => trackTrashRestored(TrashToolSegmentType.Headlines, headlineId))
    )
  );

  trackTrashDeleteForever$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.permanentDeleteHeadline),
      map(({ headlineId }) => trackTrashDeleteForever(TrashToolSegmentType.Headlines, headlineId))
    )
  );

  /** One switch archives Headlines and Cascading Messages together, so one click is one headline event. */
  trackArchiveToggleClicked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterServiceActions.archiveToggleClicked),
      concatLatestFrom(() => [
        this.store.select(selectIsHeadlinesUrl),
        this.store.select(selectArchiveTrackingMeeting),
      ]),
      filter(([, isInHeadlines]) => isInHeadlines),
      map(([{ showArchived }, , meeting]) =>
        trackArchiveViewToggled({
          itemType: 'headline',
          control: 'toggle',
          isArchived: showArchived,
          pageSource: 'headlinesPage',
          meeting,
        })
      )
    )
  );
}
